import { User } from 'firebase/auth'
import { UserProfile, UserReview, UserActivity, UserStats } from '../types/user'
import { isMock, db } from './firebase'
import { logger } from './logger'
import { groupService } from './groupService'
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  DocumentData,
  QueryDocumentSnapshot,
  Timestamp,
  QuerySnapshot,
  arrayUnion,
  arrayRemove,
  increment,
  writeBatch
} from 'firebase/firestore'

const USERS_COLLECTION = 'users'

// Mock data generation
export const generateMockUserProfile = (firebaseUser: User): UserProfile => {
  const displayName = firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Usuário'
  
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName,
    photoURL: firebaseUser.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=bfa757&color=000000`,
    phoneNumber: '+1 (555) 123-4567',
    phoneVerified: true,
    city: 'Orlando',
    state: 'FL',
    bio: 'Brasileiro vivendo nos EUA, apaixonado por conectar pessoas e oportunidades. Especializado em imóveis e carros.',
    location: 'Orlando, FL',
    memberSince: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000), // 180 days ago
    lastActive: new Date(),
    isVerified: true,
    isAdmin: false,
    role: 'user',
    listingsCount: 12,
    groupsCount: 8,
    reviewsCount: 24,
    averageRating: 4.7,
    preferences: {
      emailNotifications: true,
      pushNotifications: true,
      language: 'pt',
      theme: 'light',
    },
    socialLinks: {
      whatsapp: 'https://wa.me/15551234567',
      instagram: 'https://instagram.com/usuario',
      facebook: 'https://facebook.com/usuario',
    }
  }
}

export const generateMockReviews = (): UserReview[] => {
  const reviewers = [
    { id: 'rev-1', name: 'Carlos Mendes', photo: 'https://i.pravatar.cc/150?img=3' },
    { id: 'rev-2', name: 'Ana Costa', photo: 'https://i.pravatar.cc/150?img=4' },
    { id: 'rev-3', name: 'Pedro Alves', photo: 'https://i.pravatar.cc/150?img=5' },
    { id: 'rev-4', name: 'Mariana Lima', photo: 'https://i.pravatar.cc/150?img=6' },
    { id: 'rev-5', name: 'Lucas Santos', photo: 'https://i.pravatar.cc/150?img=7' },
  ]

  const listings = [
    { id: 'prop-1', title: 'Casa com 3 quartos em Orlando' },
    { id: 'car-1', title: 'Toyota Camry 2022' },
    { id: 'serv-1', title: 'Serviço de limpeza residencial' },
  ]

  return [
    {
      id: 'rev-1',
      reviewerId: reviewers[0].id,
      reviewerName: reviewers[0].name,
      reviewerPhotoURL: reviewers[0].photo,
      rating: 5,
      comment: 'Excelente vendedor! A casa era exatamente como descrita e o processo foi muito tranquilo. Recomendo!',
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      listingId: listings[0].id,
      listingTitle: listings[0].title,
    },
    {
      id: 'rev-2',
      reviewerId: reviewers[1].id,
      reviewerName: reviewers[1].name,
      reviewerPhotoURL: reviewers[1].photo,
      rating: 4,
      comment: 'Carro em ótimas condições, muito bem cuidado. Entrega rápida e atendimento excelente.',
      date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
      listingId: listings[1].id,
      listingTitle: listings[1].title,
    },
    {
      id: 'rev-3',
      reviewerId: reviewers[2].id,
      reviewerName: reviewers[2].name,
      reviewerPhotoURL: reviewers[2].photo,
      rating: 5,
      comment: 'Serviço de limpeza impecável! A equipe foi muito profissional e cuidadosa. Voltarei a contratar.',
      date: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000),
      listingId: listings[2].id,
      listingTitle: listings[2].title,
    },
    {
      id: 'rev-4',
      reviewerId: reviewers[3].id,
      reviewerName: reviewers[3].name,
      reviewerPhotoURL: reviewers[3].photo,
      rating: 5,
      comment: 'Ótima experiência de compra. O vendedor foi muito atencioso e esclareceu todas as dúvidas.',
      date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'rev-5',
      reviewerId: reviewers[4].id,
      reviewerName: reviewers[4].name,
      reviewerPhotoURL: reviewers[4].photo,
      rating: 4,
      comment: 'Processo rápido e eficiente. Recomendo para quem busca praticidade e confiança.',
      date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    },
  ]
}

export const generateMockActivities = (): UserActivity[] => {
  const now = new Date()
  return [
    {
      id: 'act-1',
      type: 'listing_created',
      title: 'Novo anúncio criado',
      description: 'Casa com 3 quartos em Orlando foi publicada',
      date: new Date(now.getTime() - 2 * 60 * 60 * 1000), // 2 hours ago
      link: '/listings/prop-1',
    },
    {
      id: 'act-2',
      type: 'group_joined',
      title: 'Entrou em um grupo',
      description: 'Grupo: Brasileiros em Orlando',
      date: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
      link: '/groups/group-1',
    },
    {
      id: 'act-3',
      type: 'post_created',
      title: 'Publicou no fórum',
      description: 'Dicas para comprar carro nos EUA',
      date: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      link: '/groups/group-1/post-1',
    },
    {
      id: 'act-4',
      type: 'review_received',
      title: 'Recebeu uma avaliação',
      description: '5 estrelas de Carlos Mendes',
      date: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      link: '/profile#reviews',
    },
    {
      id: 'act-5',
      type: 'message_sent',
      title: 'Mensagem enviada',
      description: 'Para Ana Costa sobre carro à venda',
      date: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000), // 4 days ago
    },
    {
      id: 'act-6',
      type: 'comment_added',
      title: 'Comentou em um post',
      description: 'No tópico "Mudança para os EUA"',
      date: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
      link: '/groups/group-2/post-3',
    },
    {
      id: 'act-7',
      type: 'listing_updated',
      title: 'Anúncio atualizado',
      description: 'Preço ajustado para Toyota Camry',
      date: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000), // 6 days ago
      link: '/listings/car-1',
    },
  ]
}

export const generateMockStats = (): UserStats => {
  return {
    totalListings: 12,
    activeListings: 8,
    soldListings: 4,
    totalGroups: 8,
    totalPosts: 42,
    totalComments: 156,
    totalMessages: 89,
    averageResponseTime: 2.5,
    completionRate: 92,
  }
}

// Helper function to convert Firestore data to UserProfile
const firestoreToUserProfile = (docId: string, data: DocumentData): UserProfile => {
  const convertTimestamp = (timestamp: unknown) => 
    timestamp instanceof Timestamp
      ? timestamp.toDate() 
      : new Date()

  return {
    uid: docId,
    email: data.email || '',
    displayName: data.displayName || null,
    photoURL: data.photoURL || null,
    phoneNumber: data.phoneNumber || null,
    phoneVerified: data.phoneVerified || false,
    city: data.city || null,
    state: data.state || null,
    bio: data.bio || null,
    location: data.location || null,
    memberSince: convertTimestamp(data.memberSince),
    lastActive: convertTimestamp(data.lastActive),
    isVerified: data.isVerified || false,
    isAdmin: data.isAdmin || false,
    role: data.role || 'user',
    listingsCount: data.listingsCount || 0,
    groupsCount: data.groupsCount || 0,
    reviewsCount: data.reviewsCount || 0,
    averageRating: data.averageRating || null,
    preferences: {
      emailNotifications: data.preferences?.emailNotifications ?? true,
      pushNotifications: data.preferences?.pushNotifications ?? true,
      language: data.preferences?.language || 'pt',
      theme: data.preferences?.theme || 'light',
    },
    socialLinks: data.socialLinks || {},
    followers: data.followers || [],
    following: data.following || [],
    followersCount: data.followersCount || 0,
    followingCount: data.followingCount || 0,
  }
}

// Helper function to convert UserProfile to Firestore data
const userProfileToFirestore = (profile: Partial<UserProfile>) => {
  const data: DocumentData = {}
  
  if (profile.email !== undefined) data.email = profile.email
  if (profile.displayName !== undefined) data.displayName = profile.displayName
  if (profile.photoURL !== undefined) data.photoURL = profile.photoURL
  if (profile.phoneNumber !== undefined) data.phoneNumber = profile.phoneNumber
  if (profile.phoneVerified !== undefined) data.phoneVerified = profile.phoneVerified
  if (profile.city !== undefined) data.city = profile.city
  if (profile.state !== undefined) data.state = profile.state
  if (profile.bio !== undefined) data.bio = profile.bio
  if (profile.location !== undefined) data.location = profile.location
  if (profile.isVerified !== undefined) data.isVerified = profile.isVerified
  if (profile.isAdmin !== undefined) data.isAdmin = profile.isAdmin
  if (profile.role !== undefined) data.role = profile.role
  if (profile.listingsCount !== undefined) data.listingsCount = profile.listingsCount
  if (profile.groupsCount !== undefined) data.groupsCount = profile.groupsCount
  if (profile.reviewsCount !== undefined) data.reviewsCount = profile.reviewsCount
  if (profile.averageRating !== undefined) data.averageRating = profile.averageRating
  if (profile.preferences !== undefined) data.preferences = profile.preferences
  if (profile.socialLinks !== undefined) data.socialLinks = profile.socialLinks
  if (profile.followers !== undefined) data.followers = profile.followers
  if (profile.following !== undefined) data.following = profile.following
  if (profile.followersCount !== undefined) data.followersCount = profile.followersCount
  if (profile.followingCount !== undefined) data.followingCount = profile.followingCount
  
  // Always update lastActive timestamp
  data.lastActive = serverTimestamp()

  // Only set memberSince if creating new profile
  if (profile.memberSince) {
    data.memberSince = profile.memberSince instanceof Date ? profile.memberSince : new Date(profile.memberSince)
  }

  return data
}

// Real Firebase functions
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  if (isMock) {
    // Return mock profile for mock mode
    const mockUser: User = {
      uid,
      email: 'user@example.com',
      displayName: 'Mock User',
      photoURL: null,
      emailVerified: true,
      isAnonymous: false,
      metadata: {},
      providerData: [],
      refreshToken: '',
      tenantId: null,
      providerId: 'mock',
      phoneNumber: null,
      delete: async () => {},
      getIdToken: async () => 'mock-token',
      getIdTokenResult: async () => ({ token: 'mock-token', expirationTime: '', issuedAtTime: '', authTime: '', signInProvider: '', signInSecondFactor: null, claims: {} }),
      reload: async () => {},
      toJSON: () => ({}),
    }
    return generateMockUserProfile(mockUser)
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid)
    const userDoc = await getDoc(userRef)
    
    if (!userDoc.exists()) {
      return null
    }
    
    return firestoreToUserProfile(userDoc.id, userDoc.data())
  } catch (error) {
    logger.error('Error fetching user profile from Firestore:', error)
    return null
  }
}

export const createUserProfile = async (uid: string, email: string, displayName: string, photoURL?: string, options?: { phoneNumber?: string, city?: string, state?: string }): Promise<void> => {
  if (isMock) {
    logger.log('Mock: creating user profile', uid, options)
    return
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid)
    const profileData = userProfileToFirestore({
      uid,
      email,
      displayName,
      photoURL: photoURL || null,
      phoneNumber: options?.phoneNumber || null,
      phoneVerified: false,
      city: options?.city || null,
      state: options?.state || null,
      bio: null,
      location: null,
      memberSince: new Date(),
      lastActive: new Date(),
      isVerified: false,
      isAdmin: false,
      role: 'user',
      listingsCount: 0,
      groupsCount: 0,
      reviewsCount: 0,
      averageRating: null,
      preferences: {
        emailNotifications: true,
        pushNotifications: true,
        language: 'pt',
        theme: 'light',
      },
      socialLinks: {},
    })
    
    await setDoc(userRef, profileData)
  } catch (error) {
    logger.error('Error creating user profile in Firestore:', error)
    throw error
  }
}

export const updateUserProfile = async (uid: string, updates: Partial<UserProfile>): Promise<void> => {
  if (isMock) {
    logger.log('Mock: updating user profile', uid, updates)
    return
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid)
    const updateData = userProfileToFirestore(updates)
    // Don't update memberSince when updating profile
    delete updateData.memberSince
    
    await updateDoc(userRef, updateData)
  } catch (error) {
    logger.error('Error updating user profile in Firestore:', error)
    throw error
  }
}

export const getUserReviews = async (uid: string): Promise<UserReview[]> => {
  if (isMock) {
    return generateMockReviews()
  }

  try {
    const reviewsRef = collection(db, 'reviews')
    const q = query(reviewsRef, where('userId', '==', uid), orderBy('date', 'desc'))
    const querySnapshot = await getDocs(q)
    
    const reviews: UserReview[] = []
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      reviews.push({
        id: doc.id,
        reviewerId: data.reviewerId,
        reviewerName: data.reviewerName,
        reviewerPhotoURL: data.reviewerPhotoURL,
        rating: data.rating,
        comment: data.comment,
        date: data.date?.toDate() || new Date(),
        listingId: data.listingId,
        listingTitle: data.listingTitle,
      })
    })
    
    return reviews
  } catch (error) {
    logger.error('Error fetching user reviews from Firestore:', error)
    return []
  }
}

export const getUserActivities = async (uid: string): Promise<UserActivity[]> => {
  if (isMock) {
    return generateMockActivities()
  }

  try {
    const activitiesRef = collection(db, 'activities')
    const activities: UserActivity[] = []
    
    // Query: User's own activities only
    const userQuery = query(activitiesRef, where('userId', '==', uid), orderBy('date', 'desc'))
    const userSnapshot = await getDocs(userQuery)
    userSnapshot.forEach((doc) => {
      const data = doc.data()
      activities.push({
        id: doc.id,
        type: data.type,
        title: data.title,
        description: data.description,
        date: data.date?.toDate() || new Date(),
        link: data.link,
        groupId: data.groupId,
        userId: data.userId,
      })
    })
    
    // Enrich activities with group names
    const uniqueGroupIds = [...new Set(activities.map(a => a.groupId).filter(id => id))] as string[]
    if (uniqueGroupIds.length > 0) {
      const groups = await groupService.getGroups({ ids: uniqueGroupIds })
      const groupMap = new Map(groups.map(g => [g.id, g.name]))
      
      activities.forEach(activity => {
        if (activity.groupId && groupMap.has(activity.groupId)) {
          // Replace group ID with group name in description
          const groupName = groupMap.get(activity.groupId)!
          activity.description = activity.description.replace(`Grupo: ${activity.groupId}`, `Grupo: ${groupName}`)
          // If description doesn't contain the pattern, append group name
          if (!activity.description.includes(groupName) && !activity.description.includes(activity.groupId)) {
            activity.description += ` (Grupo: ${groupName})`
          }
        }
      })
    }
    
    return activities
  } catch (error) {
    logger.error('Error fetching user activities from Firestore:', error)
    return []
  }
}

export const setUserAdminStatus = async (uid: string, isAdmin: boolean): Promise<void> => {
  if (isMock) {
    logger.log('Mock: setting admin status', uid, isAdmin)
    return
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid)
    await updateDoc(userRef, { isAdmin })
  } catch (error) {
    logger.error('Error setting user admin status in Firestore:', error)
    throw error
  }
}

export const setUserRole = async (uid: string, role: 'user' | 'moderator' | 'admin'): Promise<void> => {
  if (isMock) {
    logger.log('Mock: setting user role', uid, role)
    return
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid)
    await updateDoc(userRef, { role })
  } catch (error) {
    logger.error('Error setting user role in Firestore:', error)
    throw error
  }
}

export const getUsersByRole = async (role: 'user' | 'moderator' | 'admin'): Promise<UserProfile[]> => {
  if (isMock) {
    logger.log('Mock: getting users by role', role)
    // Return mock moderators
    const mockUser: UserProfile = {
      uid: 'mock-moderator-1',
      email: 'moderator@example.com',
      displayName: 'Moderador Exemplo',
      photoURL: null,
      phoneNumber: null,
      phoneVerified: false,
      city: 'Orlando',
      state: 'FL',
      bio: 'Moderador da comunidade',
      location: 'Orlando, FL',
      memberSince: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000),
      lastActive: new Date(),
      isVerified: true,
      isAdmin: role === 'admin',
      role,
      listingsCount: 5,
      groupsCount: 3,
      reviewsCount: 12,
      averageRating: 4.8,
      preferences: {
        emailNotifications: true,
        pushNotifications: true,
        language: 'pt',
        theme: 'light',
      },
      socialLinks: {},
    }
    return [mockUser]
  }

  try {
    const usersRef = collection(db, USERS_COLLECTION)
    if (role === 'admin') {
      // Buscar usuários com role 'admin' OU isAdmin true
      const [roleSnapshot, adminSnapshot] = await Promise.all([
        getDocs(query(usersRef, where('role', '==', 'admin'))),
        getDocs(query(usersRef, where('isAdmin', '==', true)))
      ])
      const usersMap = new Map<string, UserProfile>()
       const addToMap = (doc: QueryDocumentSnapshot<DocumentData>) => {
        if (!usersMap.has(doc.id)) {
          usersMap.set(doc.id, firestoreToUserProfile(doc.id, doc.data()))
        }
      }
      roleSnapshot.forEach(addToMap)
      adminSnapshot.forEach(addToMap)
      return Array.from(usersMap.values())
    } else {
      const q = query(usersRef, where('role', '==', role))
      const querySnapshot = await getDocs(q)
      const users: UserProfile[] = []
      querySnapshot.forEach((doc) => {
        users.push(firestoreToUserProfile(doc.id, doc.data()))
      })
      return users
    }
  } catch (error) {
    logger.error('Error fetching users by role from Firestore:', error)
    return []
  }
}

export const searchUsers = async (searchTerm: string): Promise<UserProfile[]> => {
  if (isMock) {
    logger.log('Mock: searching users', searchTerm)
    // Return empty array for mock
    return []
  }

  try {
    const usersRef = collection(db, USERS_COLLECTION)
    const searchLower = searchTerm.toLowerCase().trim()
    
    // Busca por email exato (case-insensitive via lowercasing no app)
    const emailQuery = query(usersRef, where('email', '==', searchLower))
    // Busca por displayName que começa com o termo (prefixo, case-sensitive)
    const nameQuery = query(
      usersRef, 
      where('displayName', '>=', searchTerm),
      where('displayName', '<=', searchTerm + '\uf8ff')
    )
    
    const [emailSnapshot, nameSnapshot] = await Promise.all([
      getDocs(emailQuery),
      getDocs(nameQuery)
    ])
    
    const usersMap = new Map<string, UserProfile>()
    
    const addUsersFromSnapshot = (snapshot: QuerySnapshot) => {
      snapshot.forEach((doc) => {
        const user = firestoreToUserProfile(doc.id, doc.data())
        if (!usersMap.has(user.uid)) {
          usersMap.set(user.uid, user)
        }
      })
    }
    
    addUsersFromSnapshot(emailSnapshot)
    addUsersFromSnapshot(nameSnapshot)
    
    return Array.from(usersMap.values())
  } catch (error) {
    logger.error('Error searching users from Firestore:', error)
    return []
  }
}

export const getUserProfileByEmail = async (email: string): Promise<UserProfile | null> => {
  if (isMock) {
    logger.log('Mock: getting user profile by email', email)
    return null
  }

  try {
    const usersRef = collection(db, USERS_COLLECTION)
    const q = query(usersRef, where('email', '==', email.toLowerCase().trim()))
    const querySnapshot = await getDocs(q)
    
    if (querySnapshot.empty) {
      return null
    }
    
    const doc = querySnapshot.docs[0]
    return firestoreToUserProfile(doc.id, doc.data())
  } catch (error) {
    logger.error('Error fetching user profile by email from Firestore:', error)
    return null
  }
}

export const getTotalUsersCount = async (): Promise<number> => {
  logger.log('getTotalUsersCount - isMock:', isMock)
  if (isMock) {
    logger.log('Returning mock count: 1243')
    return 1243
  }

  try {
    logger.log('Fetching real users count from Firestore...')
    const usersRef = collection(db, USERS_COLLECTION)
    const snapshot = await getDocs(usersRef)
    logger.log('Firestore users count:', snapshot.size)
    return snapshot.size
  } catch (error) {
    logger.error('Error getting total users count from Firestore:', error)
    return 0
  }
}

export const getActiveUsersCount = async (): Promise<number> => {
  if (isMock) {
    logger.log('Mock: getting active users count')
    return 987
  }

  try {
    const usersRef = collection(db, USERS_COLLECTION)
    // Consider active users those who were active in the last 30 days
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    const q = query(usersRef, where('lastActive', '>=', thirtyDaysAgo))
    const snapshot = await getDocs(q)
    return snapshot.size
  } catch (error) {
    logger.error('Error getting active users count from Firestore:', error)
    return 0
  }
}

export const getUserStats = async (uid: string): Promise<UserStats> => {
  if (isMock) {
    return generateMockStats()
  }

  try {
    // Collection names (should match those in groupService)
    const GROUP_POSTS_COLLECTION = 'groupPosts'
    const GROUP_COMMENTS_COLLECTION = 'groupComments'
    const GROUP_MEMBERS_COLLECTION = 'groupMembers'
    // Note: messages collection may be different
    
    // Get user's posts count
    const postsRef = collection(db, GROUP_POSTS_COLLECTION)
    const postsQuery = query(postsRef, where('author.id', '==', uid))
    const postsSnapshot = await getDocs(postsQuery)
    const totalPosts = postsSnapshot.size
    
    // Get user's comments count
    const commentsRef = collection(db, GROUP_COMMENTS_COLLECTION)
    const commentsQuery = query(commentsRef, where('author.id', '==', uid))
    const commentsSnapshot = await getDocs(commentsQuery)
    const totalComments = commentsSnapshot.size
    
    // Get user's groups count (membership)
    const membersRef = collection(db, GROUP_MEMBERS_COLLECTION)
    const membersQuery = query(membersRef, where('userId', '==', uid))
    const membersSnapshot = await getDocs(membersQuery)
    const totalGroups = membersSnapshot.size
    
    // Get user's listings count (from listings collection)
    const listingsRef = collection(db, 'listings')
    const listingsQuery = query(listingsRef, where('userId', '==', uid))
    const listingsSnapshot = await getDocs(listingsQuery)
    const totalListings = listingsSnapshot.size
    // Count active listings (status === 'active')
    const activeListings = listingsSnapshot.docs.filter(doc => doc.data().status === 'active').length
    // Count sold listings (status === 'sold')
    const soldListings = listingsSnapshot.docs.filter(doc => doc.data().status === 'sold').length
    
    // Get user's reviews count (from reviews collection) - for profile updates only
    // Note: reviewsCount and averageRating are stored in UserProfile, not UserStats
    
    // Get user's messages count (from messages collection)
    // TODO: integrate with messages service
    const totalMessages = 0
    
    // For now, use mock values for derived stats
    const averageResponseTime = 2.5
    const completionRate = 92
    
    return {
      totalListings,
      activeListings,
      soldListings,
      totalGroups,
      totalPosts,
      totalComments,
      totalMessages,
      averageResponseTime,
      completionRate,
    }
  } catch (error) {
    logger.error('Error fetching user stats from Firestore:', error)
    // Fallback to mock stats
    return generateMockStats()
  }
}

export const refreshUserStats = async (uid: string): Promise<void> => {
  if (isMock) {
    logger.log('Mock: refreshing user stats', uid)
    return
  }

  try {
    const stats = await getUserStats(uid)
    const userRef = doc(db, USERS_COLLECTION, uid)
    
    // Get reviews count and average rating
    const reviewsRef = collection(db, 'reviews')
    const reviewsQuery = query(reviewsRef, where('userId', '==', uid))
    const reviewsSnapshot = await getDocs(reviewsQuery)
    const reviewsCount = reviewsSnapshot.size
    let averageRating: number | null = null
    if (reviewsCount > 0) {
      const totalRating = reviewsSnapshot.docs.reduce((sum, doc) => sum + (doc.data().rating || 0), 0)
      averageRating = totalRating / reviewsCount
    }
    
    await updateDoc(userRef, {
      listingsCount: stats.totalListings,
      groupsCount: stats.totalGroups,
      reviewsCount,
      averageRating,
      lastActive: serverTimestamp(),
    })
  } catch (error) {
    logger.error('Error refreshing user stats:', error)
  }
}

export const suspendUser = async (uid: string, suspended: boolean): Promise<void> => {
  if (isMock) {
    logger.log('Mock: suspending user', uid, suspended)
    return
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid)
    await updateDoc(userRef, { isSuspended: suspended })
  } catch (error) {
    logger.error('Error suspending user in Firestore:', error)
    throw error
  }
}

// Follow system
export const followUser = async (followerId: string, followedId: string): Promise<void> => {
  if (followerId === followedId) {
    throw new Error('Cannot follow yourself')
  }
  
  if (isMock) {
    logger.log('Mock: following user', followerId, '->', followedId)
    return
  }
  
  try {
    const batch = writeBatch(db)
    
    // Add followedId to follower's following array
    const followerRef = doc(db, USERS_COLLECTION, followerId)
    batch.update(followerRef, {
      following: arrayUnion(followedId),
      followingCount: increment(1)
    })
    
    // Add followerId to followed's followers array
    const followedRef = doc(db, USERS_COLLECTION, followedId)
    batch.update(followedRef, {
      followers: arrayUnion(followerId),
      followersCount: increment(1)
    })
    
    await batch.commit()
  } catch (error) {
    logger.error('Error following user in Firestore:', error)
    throw error
  }
}

export const unfollowUser = async (followerId: string, followedId: string): Promise<void> => {
  if (followerId === followedId) {
    throw new Error('Cannot unfollow yourself')
  }
  
  if (isMock) {
    logger.log('Mock: unfollowing user', followerId, '->', followedId)
    return
  }
  
  try {
    const batch = writeBatch(db)
    
    // Remove followedId from follower's following array
    const followerRef = doc(db, USERS_COLLECTION, followerId)
    batch.update(followerRef, {
      following: arrayRemove(followedId),
      followingCount: increment(-1)
    })
    
    // Remove followerId from followed's followers array
    const followedRef = doc(db, USERS_COLLECTION, followedId)
    batch.update(followedRef, {
      followers: arrayRemove(followerId),
      followersCount: increment(-1)
    })
    
    await batch.commit()
  } catch (error) {
    logger.error('Error unfollowing user in Firestore:', error)
    throw error
  }
}

export const getFollowers = async (userId: string): Promise<UserProfile[]> => {
  if (isMock) {
    logger.log('Mock: getting followers for', userId)
    return []
  }
  
  try {
    const userRef = doc(db, USERS_COLLECTION, userId)
    const userDoc = await getDoc(userRef)
    if (!userDoc.exists()) return []
    
    const data = userDoc.data()
    const followerIds = data.followers || []
    
    if (followerIds.length === 0) return []
    
    // Fetch each follower's profile
    const followersPromises = followerIds.map(async (id: string) => {
      const followerRef = doc(db, USERS_COLLECTION, id)
      const followerDoc = await getDoc(followerRef)
      if (followerDoc.exists()) {
        return firestoreToUserProfile(followerDoc.id, followerDoc.data())
      }
      return null
    })
    
    const followers = await Promise.all(followersPromises)
    return followers.filter((f): f is UserProfile => f !== null)
  } catch (error) {
    logger.error('Error getting followers from Firestore:', error)
    return []
  }
}

export const getFollowing = async (userId: string): Promise<UserProfile[]> => {
  if (isMock) {
    logger.log('Mock: getting following for', userId)
    return []
  }
  
  try {
    const userRef = doc(db, USERS_COLLECTION, userId)
    const userDoc = await getDoc(userRef)
    if (!userDoc.exists()) return []
    
    const data = userDoc.data()
    const followingIds = data.following || []
    
    if (followingIds.length === 0) return []
    
    // Fetch each followed user's profile
    const followingPromises = followingIds.map(async (id: string) => {
      const followedRef = doc(db, USERS_COLLECTION, id)
      const followedDoc = await getDoc(followedRef)
      if (followedDoc.exists()) {
        return firestoreToUserProfile(followedDoc.id, followedDoc.data())
      }
      return null
    })
    
    const following = await Promise.all(followingPromises)
    return following.filter((f): f is UserProfile => f !== null)
  } catch (error) {
    logger.error('Error getting following from Firestore:', error)
    return []
  }
}

export const isFollowing = async (followerId: string, followedId: string): Promise<boolean> => {
  if (followerId === followedId) return false
  
  if (isMock) {
    logger.log('Mock: checking if following', followerId, followedId)
    return false
  }
  
  try {
    const userRef = doc(db, USERS_COLLECTION, followerId)
    const userDoc = await getDoc(userRef)
    if (!userDoc.exists()) return false
    
    const data = userDoc.data()
    const following = data.following || []
    return following.includes(followedId)
  } catch (error) {
    logger.error('Error checking follow status from Firestore:', error)
    return false
  }
}

export const getSuggestedUsersToFollow = async (userId: string, limit: number = 5): Promise<UserProfile[]> => {
  if (isMock) {
    logger.log('Mock: getting suggested users to follow for', userId)
    return []
  }
  
  try {
    // For simplicity, return random users excluding self and already followed
    const userRef = doc(db, USERS_COLLECTION, userId)
    const userDoc = await getDoc(userRef)
    if (!userDoc.exists()) return []
    
    const data = userDoc.data()
    const following = data.following || []
    const excludedIds = [userId, ...following]
    
    const usersRef = collection(db, USERS_COLLECTION)
    const usersSnapshot = await getDocs(usersRef)
    const allUsers = usersSnapshot.docs
      .filter(doc => !excludedIds.includes(doc.id))
      .slice(0, limit)
      .map(doc => firestoreToUserProfile(doc.id, doc.data()))
    
    return allUsers
  } catch (error) {
    logger.error('Error getting suggested users from Firestore:', error)
    return []
  }
}

export const getRecentUsers = async (limitCount: number = 5): Promise<UserProfile[]> => {
  if (isMock) {
    logger.log('Mock: getting recent users', limitCount);
    // Generate mock recent users
    const mockUsers: UserProfile[] = [];
    for (let i = 0; i < limitCount; i++) {
      const mockUser: UserProfile = {
        uid: `mock-recent-${i}`,
        email: `user${i}@example.com`,
        displayName: `Usuário Recente ${i}`,
        photoURL: null,
        phoneNumber: null,
        phoneVerified: false,
        city: 'Orlando',
        state: 'FL',
        bio: 'Novo usuário',
        location: 'Orlando, FL',
        memberSince: new Date(Date.now() - i * 24 * 60 * 60 * 1000), // i days ago
        lastActive: new Date(),
        isVerified: false,
        isAdmin: false,
        role: 'user',
        listingsCount: 0,
        groupsCount: 0,
        reviewsCount: 0,
        averageRating: null,
        preferences: {
          emailNotifications: true,
          pushNotifications: true,
          language: 'pt',
          theme: 'light',
        },
        socialLinks: {},
        followers: [],
        following: [],
        followersCount: 0,
        followingCount: 0,
      };
      mockUsers.push(mockUser);
    }
    return mockUsers;
  }

  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, orderBy('memberSince', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(doc => firestoreToUserProfile(doc.id, doc.data()));
    return users;
  } catch (error) {
    logger.error('Error fetching recent users from Firestore:', error);
    return [];
  }
}